import type { UseFormReturn } from 'react-hook-form';
import { useHosts } from '../../../../hooks/useHosts';
import { FormField, FormInput, FormSelect, FormTextarea, FormCheckbox, SectionCard, StepHeader } from '../../../common';
import type { RoutingRuleFormData } from '../schema';

/** General tab: name, priority, host scope, description, enabled. */
export function GeneralTab({ form }: { form: UseFormReturn<RoutingRuleFormData> }) {
  const { data: hostsResponse, isLoading: hostsLoading } = useHosts();
  const hosts = hostsResponse?.data || [];

  return (
    <div className="space-y-6">
      <StepHeader
        title="Basic Settings"
        description="Configure the basic properties of your routing rule."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Rule Name"
          required
          error={form.formState.errors.name?.message}
        >
          <FormInput
            {...form.register('name')}
            placeholder="e.g., Inbound PSTN Routing"
            hasError={!!form.formState.errors.name}
          />
        </FormField>

        <FormField
          label="Priority (1-999)"
          hint="Lower numbers = higher priority"
        >
          <FormInput
            {...form.register('priority', { valueAsNumber: true })}
            type="number"
            min={1}
            max={999}
          />
        </FormField>

        <FormField
          label="SIP Identity Scope"
          hint="Optional: apply rule only to a specific SIP identity"
        >
          <FormSelect
            {...form.register('hostId')}
            disabled={hostsLoading}
          >
            <option value="">All SIP identities</option>
            {hosts.map((host) => (
              <option key={host.id} value={host.id}>
                {host.name} {host.domain ? `(${host.domain})` : ''}{host.isDefault ? ' - Default' : ''}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField label="Description" className="md:col-span-2">
          <FormTextarea
            {...form.register('description')}
            rows={3}
            placeholder="Optional description for this routing rule"
          />
        </FormField>

        <FormCheckbox
          {...form.register('enabled')}
          label="Enable this routing rule"
        />
      </div>

      <SectionCard
        title="SIP request handling"
        description="Control SIP headers changed when this rule forwards a request to a SIP peer. Registered-contact delivery uses its own handling."
      >
        <div className="space-y-3">
          <FormCheckbox
            {...form.register('recordRouteEnabled')}
            label="Insert Record-Route"
          />
          <FormCheckbox
            {...form.register('decrementMaxForwards')}
            label="Decrement Max-Forwards"
          />
        </div>
      </SectionCard>
    </div>
  );
}
